import React from "react";
import { Button } from "@/components";
import { Eye, Calendar, Bot, Layers, Megaphone, Share2, Sparkles } from "lucide-react";
import type { ContentStrategy } from "../interfaces";

interface StrategyListProps {
  strategies: ContentStrategy[];
  onActivate: (id: number) => void;
  onView: (strategy: ContentStrategy) => void;
  /** Called when user clicks Generate for an ACTIVE plan. */
  onGenerate?: (strategyId: number) => void;
  /** Strategy ID currently generating (disables that row's Generate button). */
  generatingStrategyId?: number | null;
  isLoading?: boolean;
}

export const StrategyList: React.FC<StrategyListProps> = ({
  strategies,
  onActivate,
  onView,
  onGenerate,
  generatingStrategyId = null,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (strategies.length === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-input bg-muted/20 py-16 px-6 text-center">
        <p className="text-muted-foreground mb-1 font-medium">No content plans yet</p>
        <p className="text-sm text-muted-foreground">
          Create your first plan to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {strategies.map((strategy) => {
        const name = strategy.name.trim() || "Unnamed plan";
        const description = strategy.description?.trim();
        const agents = strategy.agentIds?.length ?? 0;
        const brands = strategy.brandIds?.length ?? 0;
        const campaigns = strategy.campaignIds?.length ?? 0;
        const integrations = strategy.integrationIds?.length ?? 0;

        return (
          <div
            key={strategy.id}
            className="group rounded-xl border-2 border-input bg-card p-5 transition-all hover:border-primary/30 hover:shadow-sm"
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => onView(strategy)}
              >
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-foreground truncate">
                    {name}
                  </h3>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                      strategy.status === "ACTIVE"
                        ? "bg-green-500/10 text-green-600 dark:text-green-400"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {strategy.status}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {strategy.mode}
                  </span>
                </div>
                {description && (
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {description}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Bot className="h-3.5 w-3.5" />
                    {agents} agent{agents !== 1 ? "s" : ""}
                  </span>
                  <span className="flex items-center gap-1">
                    <Layers className="h-3.5 w-3.5" />
                    {brands} brand{brands !== 1 ? "s" : ""}
                  </span>
                  <span className="flex items-center gap-1">
                    <Megaphone className="h-3.5 w-3.5" />
                    {campaigns} campaign{campaigns !== 1 ? "s" : ""}
                  </span>
                  <span className="flex items-center gap-1">
                    <Share2 className="h-3.5 w-3.5" />
                    {integrations} integration{integrations !== 1 ? "s" : ""}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Date(strategy.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onView(strategy)}
                  className="gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View
                </Button>
                {strategy.status === "ACTIVE" && onGenerate && (
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onGenerate(strategy.id);
                    }}
                    disabled={generatingStrategyId === strategy.id}
                    className="gap-2"
                  >
                    <Sparkles className="h-4 w-4" />
                    {generatingStrategyId === strategy.id
                      ? "Generating…"
                      : "Generate"}
                  </Button>
                )}
                {strategy.status === "DRAFT" && (
                  <Button size="sm" onClick={() => onActivate(strategy.id)}>
                    Activate
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
